import type { QueryClient } from '@tanstack/react-query';
import {
  createRootRouteWithContext,
  createRoute,
  lazyRouteComponent,
  Outlet,
} from '@tanstack/react-router';
import { SessionGate } from './session-gate';

/**
 * Route skeleton shared by every interface. Interfaces attach their own subtree under
 * `authedRoute` (see `src/interfaces/<name>/routes.tsx`); every page component is lazy so
 * each interface is its own chunk.
 */
export const rootRoute = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  component: Outlet,
});

export const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  // `error` / `step` come back from Sign in with Google (ADR-0040).
  validateSearch: (
    search: Record<string, unknown>,
  ): { redirect?: string; error?: string; step?: string } => ({
    ...(typeof search.redirect === 'string' && { redirect: search.redirect }),
    ...(typeof search.error === 'string' && { error: search.error }),
    ...(typeof search.step === 'string' && { step: search.step }),
  }),
  component: lazyRouteComponent(() => import('./login-page'), 'LoginPage'),
});

/** Pathless layout: everything below requires a signed-in session. */
export const authedRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'authed',
  component: SessionGate,
});

export const launcherRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/',
  component: lazyRouteComponent(() => import('./launcher-page'), 'LauncherPage'),
});
